//// npm stuff
// angular
import {DatePipe} from '@angular/common';
import {HTTP_INTERCEPTORS, HttpClientModule, HttpClient} from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, SecurityContext } from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
// common
import { AuthorizationService } from "src/app/services/authorization/authorization.service";

// miscellaneous
import {NgxDatatableModule} from '@swimlane/ngx-datatable';
import {OAuthModule} from 'angular-oauth2-oidc';
import {MatSelectInfiniteScrollModule} from 'ng-mat-select-infinite-scroll';
import {ClipboardModule} from 'ngx-clipboard';
import {CookieService} from 'ngx-cookie-service';
import {MarkdownModule} from 'ngx-markdown';
// environment
import {environment} from '../environments/environment';
//// our stuff
// app setup
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
// commons
import {
    AutocompleteComponent,
    CallbackPipe,
    ChipPopupComponent,
    CountryCodeComponent,
    FaqPopupComponent,
    FullPreviewComponent,
    GridComponent,
    HourglassComponent,
    InProgressMSGComponent,
    RecipientsComponent,
    SchedulerComponent,
    SpinnerButtonComponent,
    TimezoneComponent,
    UtilsModule,
    WeeklyScheduleComponent,
    ConfirmationDialogComponent
} from './components';
import { HeaderModule } from './components/header/header.module';
import { SidebarModule } from './components/sidebar/sidebar.module';
// interceptors
import {ApiInterceptor} from './interceptors/api-interceptor';
import {ErrorInterceptor} from './interceptors/error-interceptor';
import { SentryErrorHandler } from './interceptors/sentry-error-handler';
import {AngularMaterialModule} from './material-module';
// screens
import {
    ApiKeyComponent,
    CategoryDeleteComponent,
    CategoryPopupComponent,
    CopyTokenDialogComponent,
    DeclineReasonComponent,
    DeclineReasonsComponent,
    DeleteDeviceDialogComponent,
    DeleteGroupsDialogComponent,
    DevicesComponent,
    FaqComponent,
    GroupsComponent,
    GroupTypesComponent,
    IndividualDeleteComponent,
    ContactWarningDialogComponent,
    IndividualsComponent,
    MessageUIComponent,
    MonitorInfoComponent,
    MonitorUIComponent,
    QuestionCreateComponent,
    QuestionDeleteComponent,
    QuestionUpdateComponent,
    ScheduleCreateComponent,
    ScheduleUpdateComponent,
    SendMessageComponent,
    SettingsComponent,
    TagInfoPopupComponent,
    TemplatesComponent,
    TemplatesDeleteComponent,
    TimerComponent,
    UserProfileComponent,
    GroupTypePopupComponent,
    ContactTypePopupComponent,
    RateLimitOverridePopupComponent,
    GroupTypeWarningDialogComponent
} from './screens';
// services
import {AppInitializerService} from './services';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {DateTimePickerModule} from './components/date-time-picker/date-time-picker.module';
import {DateIntervalTimePickerModule} from './components/date-interval-time-picker/date-interval-time-picker.module';
import { LinkConverterPipe } from './pipes/link-converter/link-converter.pipe';
import {SectionModule} from './components/layouts/section/section.module';
import {FormContainerModule} from './components/layouts/form-container/form-container.module';
import {SectionContainerModule} from './components/layouts/section-container/section-container.module';
import {InfoMessageModule} from './components/layouts/info-message/info-message.module';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {NoResourcesModule} from './components/layouts/no-resources/no-resources.module';
import {DirectivesModule} from './directives/directives.module';
import {DarkModeToggleComponent} from "./components/dark-mode-toggle/dark-mode-toggle.component";
import {MatButtonToggleModule} from "@angular/material/button-toggle";
import { WarningPopupComponent } from './screens/send-message-ui/warning-popup/warning-popup.component';
import { ConfigModule } from './services/config/config.module';
import { AccessDeniedComponent } from './screens/access-denied/access-denied.component';
import { SnackbarListComponent } from './components/snackbar-list/snackbar-list.component';
import { TestContactPopupComponent } from './screens/admin/individuals/test-contact-popup/test-contact-popup.component';


@NgModule({
    declarations: [
        AppComponent,
        MonitorUIComponent,
        MessageUIComponent,
        ScheduleUpdateComponent,
        ScheduleCreateComponent,
        WeeklyScheduleComponent,
        SchedulerComponent,
        DeleteGroupsDialogComponent,
        IndividualsComponent,
        GroupTypesComponent,
        TemplatesComponent,
        GroupsComponent,
        DevicesComponent,
        CountryCodeComponent,
        SendMessageComponent,
        RecipientsComponent,
        CallbackPipe,
        InProgressMSGComponent,
        DeleteDeviceDialogComponent,
        FaqComponent,
        TemplatesDeleteComponent,
        UserProfileComponent,
        TimerComponent,
        IndividualDeleteComponent,
        ContactWarningDialogComponent,
        AutocompleteComponent,
        GridComponent,
        MonitorInfoComponent,
        HourglassComponent,
        SettingsComponent,
        FullPreviewComponent,
        DeclineReasonsComponent,
        DeclineReasonsComponent,
        QuestionCreateComponent,
        QuestionUpdateComponent,
        QuestionDeleteComponent,
        DeclineReasonComponent,
        CategoryPopupComponent,
        ChipPopupComponent,
        FaqPopupComponent,
        CategoryDeleteComponent,
        TimezoneComponent,
        ApiKeyComponent,
        CopyTokenDialogComponent,
        TagInfoPopupComponent,
        SpinnerButtonComponent,
        LinkConverterPipe,
        ConfirmationDialogComponent,
        DarkModeToggleComponent,
        GroupTypePopupComponent,
        WarningPopupComponent,
        ContactTypePopupComponent,
        RateLimitOverridePopupComponent,
        AccessDeniedComponent,
        SnackbarListComponent,
        GroupTypeWarningDialogComponent,
        TestContactPopupComponent
    ],
    imports: [
        AngularMaterialModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        BrowserModule,
        ClipboardModule,
        ConfigModule.forRoot({config: environment}),
        FormsModule,
        HeaderModule,
        HttpClientModule,
        MarkdownModule.forRoot({ loader: HttpClient, sanitize: SecurityContext.HTML }),
        NgxDatatableModule,
        MatSelectInfiniteScrollModule,
        SidebarModule,
        ReactiveFormsModule,
        OAuthModule.forRoot(),
        OwlDateTimeModule,
        OwlNativeDateTimeModule,
        MatProgressSpinnerModule,
        UtilsModule,
        DateIntervalTimePickerModule,
        DateTimePickerModule,
        SectionModule,
        FormContainerModule,
        SectionContainerModule,
        InfoMessageModule,
        MatProgressBarModule,
        NoResourcesModule,
        DirectivesModule,
        MatButtonToggleModule
    ],
    exports: [
        DeleteGroupsDialogComponent,
        DeleteDeviceDialogComponent,
        TemplatesDeleteComponent,
        IndividualDeleteComponent,
        ContactWarningDialogComponent,
        MonitorInfoComponent,
        ScheduleUpdateComponent,
        ScheduleCreateComponent,
        WeeklyScheduleComponent,
        ChipPopupComponent,
        FaqPopupComponent,
        QuestionCreateComponent,
        QuestionDeleteComponent,
        QuestionUpdateComponent,
        DeclineReasonComponent,
        CategoryPopupComponent,
        FaqComponent,
        CategoryDeleteComponent,
        TimezoneComponent,
        TagInfoPopupComponent,
        GroupTypePopupComponent,
        ContactTypePopupComponent,
        GroupTypeWarningDialogComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [
        {provide: APP_INITIALIZER, useFactory: init_app, deps: [AppInitializerService], multi: true},
        DatePipe,
        {provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
        {provide: ErrorHandler, useClass: SentryErrorHandler },
        CookieService,
        AuthorizationService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}

// @ts-ignore
export function init_app(appInitializer: AppInitializerService) {
    return () => appInitializer.initApp();
}
